import React, { useState, useEffect } from 'react';
import { bountyService } from '../../services/bountyService';
import {
  GiftIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

const RewardsPage = () => {
  const [rewards, setRewards] = useState([]);
  const [claimedRewards, setClaimedRewards] = useState([]);
  const [stats, setStats] = useState({
    totalKgSold: 0,
    approvedReferrals: 0,
    totalReferrals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadRewardsData();
  }, []);

  const loadRewardsData = async () => {
    try {
      setLoading(true);
      const [rewardsResponse, statsResponse, claimedResponse] = await Promise.all([
        bountyService.getUserAvailableRewards(),
        bountyService.getUserRewardStats(),
        bountyService.getUserClaimedRewards(),
      ]);
      
      // Handle API response structure
      const rewardsData = rewardsResponse.data?.data || rewardsResponse.data || [];
      const statsData = statsResponse.data?.data || statsResponse.data || {};
      const claimedData = claimedResponse.data?.data || claimedResponse.data || [];
      
      setRewards(Array.isArray(rewardsData) ? rewardsData : []);
      setClaimedRewards(Array.isArray(claimedData) ? claimedData : []);
      setStats({
        totalKgSold: statsData.totalKgSold || 0,
        approvedReferrals: statsData.approvedReferrals || 0,
        totalReferrals: statsData.totalReferrals || 0,
      });
    } catch (error) {
      console.error('Error loading rewards data:', error);
      setMessage('Error loading rewards data');
      setRewards([]);
      setClaimedRewards([]);
      setStats({
        totalKgSold: 0,
        approvedReferrals: 0,
        totalReferrals: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClaimReward = async (bountyId) => {
    try {
      setMessage('');
      await bountyService.claimReward(bountyId);
      setMessage('Reward claimed successfully!');
      loadRewardsData(); // Refresh data
    } catch (error) {
      console.error('Error claiming reward:', error);
      setMessage(error.response?.data?.message || 'Error claiming reward');
    }
  };

  const getStatusIcon = (reward) => {
    if (reward.isClaimed) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else if (reward.isEligible) {
      return <GiftIcon className="h-5 w-5 text-blue-500" />;
    } else {
      return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusText = (reward) => {
    if (reward.isClaimed) {
      return 'Claimed';
    } else if (reward.isEligible) {
      return 'Available';
    } else {
      return 'Not Eligible';
    }
  };

  const getStatusColor = (reward) => {
    if (reward.isClaimed) {
      return 'bg-green-100 text-green-800';
    } else if (reward.isEligible) {
      return 'bg-blue-100 text-blue-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  };

  const getClaimedStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getClaimedStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rewards & Bounties</h1>
        <p className="text-gray-600">Earn rewards by selling materials and referring friends</p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes('successfully')
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TruckIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Sold</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalKgSold} kg</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Approved Referrals</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.approvedReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <GiftIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Referrals</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalReferrals}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Claimed Rewards</p>
              <p className="text-2xl font-semibold text-gray-900">{claimedRewards.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rewards Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Rewards</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rewards.map((reward) => (
            <div key={reward._id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                {reward.image ? (
                  <img
                    src={reward.image}
                    alt={reward.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <PhotoIcon className="h-12 w-12 text-gray-400" />
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{reward.title}</h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reward)}`}>
                    {getStatusText(reward)}
                  </span>
                </div>

                <p className="text-gray-600 text-sm mb-3">{reward.description}</p>

                {/* Requirement */}
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-700">Requirement: </span>
                  <span className="text-sm text-gray-600">
                    {reward.requirement.value} {reward.requirement.unit}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Progress</span>
                    <span>
                      {reward.type === 'sale' 
                        ? `${Math.min(reward.userStats.totalKgSold, reward.requirement.value)} / ${reward.requirement.value} kg`
                        : `${Math.min(reward.userStats.approvedReferrals, reward.requirement.value)} / ${reward.requirement.value} people`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.min(
                          (reward.type === 'sale' 
                            ? reward.userStats.totalKgSold 
                            : reward.userStats.approvedReferrals
                          ) / reward.requirement.value * 100,
                          100
                        )}%`
                      }}
                    ></div>
                  </div>
                </div>

                {/* Reward */}
                <div className="mb-4">
                  <span className="text-sm font-medium text-gray-700">Reward: </span>
                  <span className="text-sm font-semibold text-green-600">{reward.reward.value}</span>
                </div>

                {/* Claim Button */}
                <button
                  onClick={() => handleClaimReward(reward._id)}
                  disabled={!reward.isEligible || reward.isClaimed}
                  className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                    reward.isClaimed
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : reward.isEligible
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {reward.isClaimed ? 'Reward Claimed' : 'Claim Reward'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {rewards.length === 0 && (
          <div className="text-center py-12">
            <GiftIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rewards available</h3>
            <p className="mt-1 text-sm text-gray-500">Check back later for new rewards!</p>
          </div>
        )}
      </div>

      {/* Claimed Rewards Section */}
      {claimedRewards.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Claimed Rewards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {claimedRewards.map((claimedReward) => (
              <div key={claimedReward._id} className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-green-500">
                {/* Image */}
                <div className="h-48 bg-gray-200 flex items-center justify-center">
                  {claimedReward.bounty.image ? (
                    <img
                      src={claimedReward.bounty.image}
                      alt={claimedReward.bounty.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <PhotoIcon className="h-12 w-12 text-gray-400" />
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{claimedReward.bounty.title}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getClaimedStatusColor(claimedReward.status)}`}>
                      {getClaimedStatusIcon(claimedReward.status)}
                      <span className="ml-1 capitalize">{claimedReward.status}</span>
                    </span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3">{claimedReward.bounty.description}</p>

                  {/* Reward */}
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-700">Reward: </span>
                    <span className="text-sm font-semibold text-green-600">{claimedReward.claimedReward.value}</span>
                  </div>

                  {/* Claimed Date */}
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-700">Claimed: </span>
                    <span className="text-sm text-gray-600">
                      {new Date(claimedReward.claimedAt).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Status Message */}
                  <div className="text-center">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getClaimedStatusColor(claimedReward.status)}`}>
                      {claimedReward.status === 'delivered' && '✓ Reward Delivered'}
                      {claimedReward.status === 'pending' && '⏳ Processing'}
                      {claimedReward.status === 'cancelled' && '✗ Cancelled'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsPage;
